import {
	faArrowRight,
	faBoxesStacked,
	faTruckFast,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	ImagePlaceholder,
	ResilientImage,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useDisplaySettings } from '../../../hooks/useDisplaySettings';
import { usePortalAuth } from '../../../hooks/usePortalAuth';
import { getStorefrontLastOrder } from '../../../services/portalDashboardService';
import { LastOrderSkeleton } from '../../skeletons';

/**
 * Last-Order widget — compact summary of the customer's most recent
 * order (date, total, line count, status, optional tracking link)
 * with thumbnail tiles for the top 4 lines. The whole card is a
 * link to the order detail page; the dedicated reorder action lives
 * there, NOT here, because reordering is an intent that benefits
 * from the full line-by-line view.
 *
 * Login-required.
 */
export function LastOrderWidget({
	title,
}: {
	title?: string;
}): JSX.Element | null {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { token } = usePortalAuth();
	const display = useDisplaySettings();
	const queryClient = useQueryClient();

	const queryKey = ['portal', 'dashboard', 'last-order'];
	const { data, isLoading, isError } = useQuery({
		queryKey,
		queryFn: () => getStorefrontLastOrder(token),
		enabled: token != null,
		staleTime: 60_000,
	});

	const order = data?.data ?? null;

	// Auto-hide rules: same as Reorder. Don't double the sign-in CTA
	// for anonymous visitors (Account widget covers that), don't take
	// up real estate when the customer has no orders to show.
	// PD-14691: order widgets also require the order-history surface
	// (defensive — the dashboard dispatch already skips mounting this).
	if (token == null || !display.canViewOrderHistory) {
		return null;
	}
	if (!isLoading && !isError && order == null) return null;

	return (
		<section className="w-full h-full flex flex-col bg-brand-surface border border-brand-border rounded-xl p-4 md:p-5">
			<header className="flex items-center justify-between mb-3">
				<h2 className="flex items-center gap-2 text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100">
					<FontAwesomeIcon
						icon={faBoxesStacked}
						className="text-portal-accent"
					/>
					{t(title ?? 'Your last order')}
				</h2>
				<button
					type="button"
					onClick={() => navigate('/orders')}
					className="text-xs md:text-sm text-portal-accent hover:opacity-80 inline-flex items-center gap-1"
				>
					{t('All orders')}
					<FontAwesomeIcon icon={faArrowRight} className="text-[10px]" />
				</button>
			</header>

			{isLoading ? (
				/* Shaped like the summary + thumbnail strip below (PD-14692). */
				<LastOrderSkeleton />
			) : isError || order == null ? (
				<EmptyMessage
					message={t("We couldn't load your last order right now.")}
				/>
			) : (
				<button
					type="button"
					onClick={() => navigate(`/orders/${order.id}`)}
					className="flex-1 text-left rounded-lg hover:bg-brand-input/50 transition-colors p-2 -m-2"
				>
					<div className="flex items-baseline justify-between gap-3 mb-2">
						<div>
							<p className="text-sm font-medium text-gray-900 dark:text-gray-100">
								{order.externalId != null
									? `${t('#')}${order.externalId}`
									: `${t('Order')} ${order.id}`}
							</p>
							<p className="text-xs text-brand-muted">
								{formatDate(order.date, t)}
								{/*
								 * Show line-item count rather than the raw status enum here.
								 * The status field comes back as a numeric code ("1", "2", …)
								 * which read as bare integers in the UI (e.g. "May 2, 2026 · 1"),
								 * confusing customers. Item count answers "how big was my last
								 * order?" in one glance and never needs an enum-label lookup.
								 */}
								{order.numberOfLines != null && order.numberOfLines > 0 && (
									<>
										{' • '}
										{order.numberOfLines}{' '}
										{order.numberOfLines === 1 ? t('item') : t('items')}
									</>
								)}
							</p>
						</div>
						{/* PD-14239: hide the order total when this portal user
						    cannot see pricing; the #/date/line-count stay. */}
						{display.canSeePricing && (
							<p className="text-base md:text-lg font-bold text-gray-900 dark:text-gray-100">
								{order.total != null
									? formatCurrency(Number(order.total), t)
									: t('—')}
							</p>
						)}
					</div>

					{/*
					 * Only render the image strip when at least one preview line has an
					 * image. The placeholder-text fallback (description-in-a-gray-tile)
					 * looks like an empty rectangle on small screens for SKUs that lack
					 * imagery, which makes the card feel broken. Lines without images
					 * are filtered out; if that leaves nothing, the section is hidden.
					 */}
					{(() => {
						const linesWithImages = order.previewLines.filter(
							(line): line is typeof line & { primaryImageUrl: string } =>
								line.primaryImageUrl != null,
						);
						if (linesWithImages.length === 0) return null;
						return (
							<div className="grid grid-cols-4 gap-2">
								{linesWithImages.map((line) => (
									<div
										key={line.id}
										className="aspect-square bg-brand-input rounded-md overflow-hidden flex items-center justify-center"
										title={line.description}
									>
										<ResilientImage
											src={line.primaryImageUrl}
											alt={line.description}
											className="w-full h-full object-contain"
											loading="lazy"
											onLikelyStaleUrl={() => {
												void queryClient.invalidateQueries({ queryKey });
											}}
											fallback={<ImagePlaceholder size="sm" />}
										/>
									</div>
								))}
							</div>
						);
					})()}

					{order.numberOfLines > order.previewLines.length && (
						<p className="mt-2 text-[11px] text-brand-muted">
							{t('+')}
							{order.numberOfLines - order.previewLines.length}{' '}
							{t('more line(s)')}
						</p>
					)}

					{order.trackingUrl != null && (
						<a
							href={order.trackingUrl}
							target="_blank"
							rel="noopener noreferrer"
							onClick={(e) => e.stopPropagation()}
							className="mt-3 inline-flex items-center gap-2 text-xs md:text-sm text-portal-accent hover:opacity-80"
						>
							<FontAwesomeIcon icon={faTruckFast} />
							{t('Track shipment')}
						</a>
					)}
				</button>
			)}
		</section>
	);
}

function EmptyMessage({ message }: { message: string }): JSX.Element {
	return (
		<div className="flex-1 flex items-center justify-center text-center px-4 py-6">
			<p className="text-sm text-brand-muted max-w-md">{message}</p>
		</div>
	);
}

function formatDate(
	date: Date | string | null,
	t: (s: string) => string,
): string {
	if (date == null) return t('—');
	const d = typeof date === 'string' ? new Date(date) : date;
	if (Number.isNaN(d.getTime())) return t('—');
	return d.toLocaleDateString(undefined, {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});
}

function formatCurrency(amount: number, t: (s: string) => string): string {
	if (!Number.isFinite(amount)) return t('—');
	return `${t('$')}${amount.toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`;
}
